import React, { useEffect, useMemo, useState } from 'react';
import {
  IconCalendar,
  IconClipboardType,
  IconEye,
  IconSoberLiving,
  IconWallet,
  OrangeButton,
  SmallButton,
  TooltipIcon,
} from 'components';
import { COLORS } from 'utils/colors';
import CUP from 'assets/images/cup.png';
import { ClientTasks } from './components/clientTasks';
import { getRequestData } from 'api/api';
import { endpoints } from 'api/endpoints';
import { CLIENT_STATUS, ROLE_TYPES } from 'api/constants';
import {
  formatPriceWithComma,
  getActiveWeekAndDaysSinceStart,
  getClientTaskProgressState,
} from 'utils/helpers';

export const ClientDashboard = () => {
  const [dashboardData, setDashboardData] = useState();

  useEffect(() => {
    const fetchDashboardData = async () => {
      const response = await getRequestData(endpoints.clientDashboard, ROLE_TYPES.client);
      if (response && response.data) {
        setDashboardData(response.data);
      }
    };
    fetchDashboardData();
  }, []);

  const activeDates = useMemo(() => {
    if (!dashboardData) return {};
    return getActiveWeekAndDaysSinceStart(
      dashboardData.client?.start_date,
      dashboardData.client?.end_date,
    );
  }, [dashboardData]);

  const effectiveActiveWeek = useMemo(() => {
    if (activeDates?.activeWeek !== undefined && activeDates?.activeWeek > 0) {
      return activeDates.activeWeek;
    }
    return 1;
  }, [activeDates]);

  const storeWeeks = useMemo(() => {
    if (!dashboardData?.client) return [];
    const client = dashboardData.client;
    const lastAssignedTask = [...(client?.tasks || [])].filter((t) => t.assigned_at).at(-1);
    const lastAssignedWeek = lastAssignedTask?.week ?? 0;
    const lastAssignedAt = lastAssignedTask?.assigned_at ?? null;

    const weeksArr = [];

    for (let i = 1; i < 7; i++) {
      weeksArr.push(
        singleWeekCircle(
          i,
          client.tasks?.[i - 1],
          lastAssignedWeek,
          lastAssignedAt,
          effectiveActiveWeek,
        ),
      );
    }

    return weeksArr;
  }, [dashboardData?.client, effectiveActiveWeek]);

  const findNotCompletedWeek = useMemo(() => {
    const uncompletedTask = dashboardData?.client?.tasks?.find((item) => !item.completed);
    return uncompletedTask?.week;
  }, [dashboardData?.client?.tasks]);

  const latestCompletedTask = useMemo(() => {
    return dashboardData?.client?.tasks?.filter((item) => item?.completed).at(-1);
  }, [dashboardData?.client?.tasks]);

  return (
    dashboardData && (
      <div className="flex flex-col w-full pb-20 md:pb-12">
        <div className="flex flex-col base-block w-full overflow-hidden rounded-block-radius md:rounded-[24px]">
          {/* DESKTOP SLH HEADER (shown on desktop > 900px, hidden on mobile <= 900px) */}
          <div className="flex md:hidden w-full py-7 px-[30px] items-center">
            <div className="flex gap-x-2.5 items-center">
              <div className="w-12 h-12 bg-divider rounded-full flex items-center justify-center flex-shrink-0">
                <IconSoberLiving color={COLORS.blue} />
              </div>
              <b className="text-22 nunito-title normal-case">{dashboardData.partner?.name}</b>
            </div>
            <div className="flex items-center base-block-light-shadow p-1 pl-6 mr-6 ml-auto">
              <p className="text-14 font-normal text-lightBlue mr-3">PF25 Invested</p>
              <div className="flex items-center bg-lightGreen py-1.5 pr-6 pl-3 rounded-md">
                <IconWallet color={COLORS.darkGreen} />
                <p className="text-14 font-bold ml-2 text-darkGreen">
                  $
                  {formatPriceWithComma(
                    Array.isArray(dashboardData?.client?.granted)
                      ? dashboardData?.client?.granted
                          ?.reduce((acc, curr) => acc + +curr, 0)
                          .toFixed(2)
                      : dashboardData?.client?.granted,
                  )}
                </p>
              </div>
              <p className="text-14 font-normal text-lightBlue mx-3">for</p>
              <div className="flex items-center bg-yellow py-1.5 pr-6 pl-3 rounded-md">
                <IconCalendar color="#8A5B00" />
                <p className="text-14 font-bold ml-2 text-[#8A5B00]">{activeDates?.daysSinceStart} Days</p>
              </div>
              <TooltipIcon
                className="ml-3 mr-2 cursor-pointer"
                placement="bottomRight"
                tooltipText={`PowerForward25 has covered $${formatPriceWithComma(
                  Array.isArray(dashboardData?.client?.granted)
                    ? dashboardData?.client?.granted
                        ?.reduce((acc, curr) => acc + +curr, 0)
                        .toFixed(2)
                    : dashboardData?.client?.granted,
                )} of your Sober Living Home stay over the past ${activeDates?.daysSinceStart} days through your scholarship.`}
              />
            </div>
          </div>

          {/* MOBILE SLH HEADER (hidden on desktop > 900px, shown on mobile <= 900px) */}
          <div className="hidden md:flex flex-col gap-y-3 p-4">
            <div className="flex gap-x-2.5 items-center">
              <div className="w-9 h-9 bg-divider rounded-xl flex items-center justify-center flex-shrink-0">
                <IconSoberLiving color={COLORS.blue} />
              </div>
              <b className="text-16 nunito-title normal-case truncate">{dashboardData.partner?.name}</b>
            </div>
            <div className="flex items-center justify-end gap-x-2 flex-wrap gap-y-1 self-end w-full">
              <span className="text-12 font-normal text-lightBlue whitespace-nowrap">PF25 Invested</span>
              <div className="flex items-center bg-lightGreen py-1 px-2.5 rounded-md">
                <p className="text-12 font-bold text-darkGreen">
                  $
                  {formatPriceWithComma(
                    Array.isArray(dashboardData?.client?.granted)
                      ? dashboardData?.client?.granted
                          ?.reduce((acc, curr) => acc + +curr, 0)
                          .toFixed(2)
                      : dashboardData?.client?.granted,
                  )}
                </p>
              </div>
              <span className="text-12 font-normal text-lightBlue whitespace-nowrap">for</span>
              <div className="flex items-center bg-yellow py-1 px-2.5 rounded-md">
                <p className="text-12 font-bold text-[#8A5B00]">{activeDates?.daysSinceStart} Days</p>
              </div>
            </div>
          </div>

          {/* HERO GRADIENT SECTION */}
          <div className="flex flex-col bg-green-gradient relative p-[30px] md:p-4 overflow-hidden rounded-b-block-radius md:rounded-b-[24px]">
            <div className="flex flex-col z-10 relative">
              <div className="flex items-center gap-x-3.5 md:gap-x-2">
                <p className="text-5xl md:text-2xl text-orange nunito capitalize">
                  Hello {dashboardData.profile?.firstname},
                </p>
                <span className="text-4xl md:text-2xl">👋</span>
              </div>
              <b className="text-22 md:text-16 nunito-title uppercase mt-1">SEE YOUR PROGRESS</b>
              <div className="flex gap-x-6 md:gap-x-3 mt-8 md:mt-4 mb-12 md:mb-6 overflow-x-auto py-3 px-2 md:px-1.5 no-scrollbar min-w-0 z-10 relative">
                {storeWeeks}
              </div>
            </div>
            <div
              className={`relative z-10 rounded-xl md:rounded-2xl flex self-start w-auto md:w-full max-w-[480px] md:max-w-[380px] items-center justify-between p-3 md:p-3 h-[90px] md:h-auto bg-white shadow-block-shadow gap-x-6 md:gap-x-2 ${dashboardData.client?.status === CLIENT_STATUS.not_started || !findNotCompletedWeek ? 'px-10 md:px-4' : 'pl-10 pr-3 md:px-3'}`}
            >
              <p className="text-16 md:!text-13 leading-tight font-medium text-black pr-2">
                {(dashboardData.client?.status === CLIENT_STATUS.not_started ||
                  dashboardData.client?.tasks.length === 0 ||
                  !findNotCompletedWeek)
                  ? 'There are no new tasks'
                  : 'You have an incomplete weekly form'}
              </p>
              {dashboardData.client?.status !== CLIENT_STATUS.not_started &&
                (!findNotCompletedWeek && latestCompletedTask ? (
                  <SmallButton
                    btnClassName="ant-btn-green ant-btn-mobile w-auto !h-[66px] md:!h-9 font-semibold text-15 md:!text-12 rounded-xl md:rounded-lg px-8 md:!px-4 flex-shrink-0"
                    icon={<IconEye className="inline-block md:hidden hover:text-darkGrey" color="#fff" />}
                    label="View"
                    href={`/weekly-forms/${latestCompletedTask.week}`}
                  />
                ) : (
                  findNotCompletedWeek && (
                    <OrangeButton
                      href={`/weekly-forms/${findNotCompletedWeek}`}
                      btnClassName="ant-btn-green ant-btn-mobile w-auto !h-[66px] md:!h-9 font-semibold text-15 md:!text-12 rounded-xl md:rounded-lg px-8 md:!px-4 flex-shrink-0"
                      label="Start"
                      disabled={dashboardData.client?.tasks.length === 0}
                      icon={<IconClipboardType color="#fff" className="inline-block md:hidden ml-2 mr-1 min-w-5 min-h-5" />}
                    />
                  )
                ))}
            </div>
            <div className="absolute bg-orange-gradient rounded-full w-[650px] h-[650px] md:w-[220px] md:h-[220px] top-1/2 md:-top-16 right-0 md:-right-16 translate-x-1/2 md:translate-x-0 -translate-y-1/2 md:translate-y-0 z-0 pointer-events-none" />
            <img className="absolute w-[426px] md:w-[180px] right-0 md:-right-[10px] top-4 opacity-100 md:opacity-60 pointer-events-none z-0" src={CUP} alt="cup" />
          </div>
        </div>
        <ClientTasks client={dashboardData.client} activeWeek={effectiveActiveWeek} />
      </div>
    )
  );
};

const singleWeekCircle = (index, task, lastAssignedWeek, lastAssignedAt, activeWeek) => {
  const taskState = getClientTaskProgressState(task, index, lastAssignedWeek, lastAssignedAt);
  const isCurrentWeek = index === activeWeek;

  const circleColor = () => {
    switch (taskState) {
      case 'completed':
        return 'bg-green';
      case 'pending':
        return 'bg-orange';
      case 'overdue':
        return 'bg-red';
      case 'upcoming':
      case 'current':
        return 'bg-lightGreen';
      default:
        return 'bg-grey';
    }
  };

  const circleTextColor = () => {
    switch (taskState) {
      case 'upcoming':
      case 'current':
        return 'text-green';
      default:
        return 'text-white';
    }
  };

  return (
    <div
      key={`week-${index}`}
      className={`flex items-center justify-center rounded-full w-[140px] h-[140px] md:w-[64px] md:h-[64px] flex-shrink-0 transition-transform ${circleColor()} ${
        isCurrentWeek ? 'ring-4 ring-orange/60 shadow-lg' : 'ring-4 ring-white/50 shadow-md'
      }`}
    >
      <div className="flex flex-col justify-center items-center">
        <p className={`text-5xl md:text-2xl font-semibold ${circleTextColor()}`}>{index}</p>
        <p className={`text-xl md:text-xs ${circleTextColor()}`}>Week</p>
      </div>
    </div>
  );
};
